import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { palette } from '@openx/components-core/lib/theme/paletteV2';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'recharts';
import { useDealInsightsContext } from '../../../../context/DealInsightsContext';
import { fillSpendDataGaps } from '../../../../utils/fillDataGaps';
import { type DateResolution, DateResolutionToggle } from '../../../DateResolutionToggle/DateResolutionToggle';
import { MOCK_DATA } from '../chartUtils';
import { SpendChartCore } from './components/SpendChartCore';
import { SpendTooltip } from './components/SpendTooltip';

type SpendChartProps = {
  hideHeader?: boolean;
};

export const SpendChart = ({ hideHeader = false }: SpendChartProps = {}) => {
  const { t } = useTranslation();
  const {
    dealInsights: { data, chartsLoadingState },
    chartDateResolutions,
    updateChartDateResolution,
    dateRange,
  } = useDealInsightsContext();

  const dateResolution = chartDateResolutions.spend;
  const setDateResolution = (resolution: DateResolution) => updateChartDateResolution('spend', resolution);
  const rawValues = data?.spend ?? [];

  const parsedValues = useMemo(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      return rawValues;
    }

    return fillSpendDataGaps({
      rawValues,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      dateResolution,
    });
  }, [rawValues, dateRange.startDate, dateRange.endDate, dateResolution]);

  const isLoading = !!chartsLoadingState.spend;
  const values = isLoading ? MOCK_DATA : (parsedValues ?? []);

  const shouldRenderChart = values.length > 0;

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: hideHeader ? 0 : 4, height: 420 }}
      data-test="spend-chart-container"
    >
      {!hideHeader && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h2" data-test="spend-chart-title">
            {t('Spend vs CPM')}
          </Typography>
          <DateResolutionToggle value={dateResolution} onChange={setDateResolution} disabled={isLoading} />
        </Box>
      )}

      {/* Relative and absolute wrappers are needed for chart to be responsive */}
      <Box sx={{ position: 'relative', width: '100%', height: 280 }}>
        <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, top: 0 }}>
          {shouldRenderChart && (
            <SpendChartCore
              values={values}
              isLoading={isLoading}
              showLegend={true}
              showTooltip={!isLoading}
              tooltipContent={!isLoading ? <Tooltip content={<SpendTooltip />} /> : undefined}
            />
          )}

          {!shouldRenderChart && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                width: '100%',
                gap: 0.5,
              }}
            >
              <Typography variant="body1Bold">{t('No data')}</Typography>
              <Typography variant="body1" color={palette.text.secondary}>
                {t('Try adjusting filters or date range to see the results.')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
